/**
 * ArchiveSearch - Search-as-Harvest
 *
 * Keyword-weighted ranked results with harvest-style cards.
 * Click a card to load the full source document into the workspace.
 *
 * @module @humanizer/studio/components/archive/ArchiveSearch
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { useBufferSync } from '../../contexts/BufferSyncContext';
import { usePanels } from '../../contexts/PanelContext';
import { useArchiveApi, type SearchResult } from '../../hooks/useArchiveApi';
import { createLogger } from '../../utils/debug';

const log = createLogger('ArchiveSearch');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ArchiveSearchProps {
  archiveId?: string;
  onSelectContent?: (contentId: string, type: 'node' | 'transcript') => void;
  /** Called when a highlight should appear in the workspace */
  onHighlightRequested?: (text: string) => void;
  /** Called when a thread is loaded and should navigate to a specific message index */
  onNavigateToMessage?: (index: number) => void;
  className?: string;
}

/** Filter modes for search */
type SearchFilter = 'all' | 'transcripts' | 'conversations' | 'media';

// ═══════════════════════════════════════════════════════════════════════════
// SCORING
// ═══════════════════════════════════════════════════════════════════════════

/** Compute keyword-weighted composite score (sparse/keyword weighted high) */
function reviewedScore(r: SearchResult): number {
  const sb = r.scoreBreakdown;
  if (!sb) return r.score;
  return (sb.sparseScore ?? 0) * 0.6 + (sb.denseScore ?? 0) * 0.4;
}

/** Clamp a score to 0-100% for display */
function normalizeScore(score: number | undefined): number {
  if (score == null) return 0;
  return Math.round(Math.min(Math.max(score, 0), 1) * 100);
}

/** Derive a display title from a result */
function resultTitle(r: SearchResult): string {
  return r.enrichment?.title
    || r.provenance?.threadTitle
    || r.title
    || r.text.slice(0, 60).replace(/\n/g, ' ') + (r.text.length > 60 ? '...' : '');
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function ArchiveSearch({
  archiveId,
  onSelectContent,
  onHighlightRequested,
  onNavigateToMessage,
  className = '',
}: ArchiveSearchProps): React.ReactElement {
  const api = useApi();
  const { sessionId, openNodeInContext } = useBufferSync();
  const { state: panelState, clearArchiveSearch } = usePanels();
  const archiveApi = useArchiveApi();

  // Platform detection
  const isElectron = typeof window !== 'undefined' && !!window.isElectron;

  // ALL HOOKS MUST BE CALLED UNCONDITIONALLY (Rules of Hooks)
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<SearchFilter>('all');
  const [rawResults, setRawResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Include/exclude keyword filters
  const [includeTags, setIncludeTags] = useState<string[]>([]);
  const [excludeTags, setExcludeTags] = useState<string[]>([]);
  const [includeInput, setIncludeInput] = useState('');
  const [excludeInput, setExcludeInput] = useState('');

  // Consume external search from PanelContext (e.g. "Find Similar")
  useEffect(() => {
    if (panelState.archiveSearchQuery && panelState.archiveSearchResults) {
      setQuery(panelState.archiveSearchQuery);
      setRawResults(panelState.archiveSearchResults);
      setIncludeTags([]);
      setExcludeTags([]);
      clearArchiveSearch();
    }
  }, [panelState.archiveSearchQuery, panelState.archiveSearchResults, clearArchiveSearch]);

  // Ranked results: sorted by reviewedScore descending
  const rankedResults = useMemo(() => {
    return [...rawResults].sort((a, b) => reviewedScore(b) - reviewedScore(a));
  }, [rawResults]);

  // Apply include/exclude filters
  const filteredResults = useMemo(() => {
    if (includeTags.length === 0 && excludeTags.length === 0) return rankedResults;

    return rankedResults.filter((r) => {
      const text = (r.text + ' ' + resultTitle(r)).toLowerCase();
      if (includeTags.length > 0 && !includeTags.every((t) => text.includes(t.toLowerCase()))) {
        return false;
      }
      if (excludeTags.length > 0 && excludeTags.some((t) => text.includes(t.toLowerCase()))) {
        return false;
      }
      return true;
    });
  }, [rankedResults, includeTags, excludeTags]);

  const filteredCount = rankedResults.length - filteredResults.length;

  // Debounced search - uses archiveApi which routes to local or cloud
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setRawResults([]);
        return;
      }

      // In Electron mode, we don't need a cloud sessionId
      if (!isElectron && !sessionId) {
        setError('No active session. Please wait for connection.');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const sources: string[] | undefined =
          filter === 'all' ? undefined :
          filter === 'transcripts' ? ['transcripts'] :
          filter === 'conversations' ? ['archive'] :
          filter === 'media' ? ['media'] : undefined;

        // Use archiveApi.searchHybrid for combined keyword + semantic results
        const response = await archiveApi.searchHybrid(searchQuery, {
          sourceTypes: sources,
          limit: 50,
        });

        setRawResults(response.results);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
        setRawResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, archiveApi, filter, isElectron]
  );

  // Debounce query changes
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (query.trim()) {
      searchTimeoutRef.current = setTimeout(() => performSearch(query), 300);
    } else {
      setRawResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [query, performSearch]);

  // Handle filter change
  const handleFilterChange = useCallback(
    (newFilter: SearchFilter) => {
      setFilter(newFilter);
      if (query.trim()) performSearch(query);
    },
    [query, performSearch]
  );

  // Click card → load full document via openNodeInContext (unified)
  const handleCardClick = useCallback(
    async (result: SearchResult) => {
      onSelectContent?.(result.id, 'node');
      setLoadingId(result.id);

      try {
        const snippet = result.text.slice(0, 200);
        await openNodeInContext({
          nodeId: result.id,
          highlightText: snippet,
        });
      } catch (err) {
        log.error('Failed to load full document:', err);
      } finally {
        setLoadingId(null);
      }
    },
    [openNodeInContext, onSelectContent]
  );

  // Score pill helper — uses normalizeScore for clamped 0-100%
  const scorePill = (label: string, value: number | undefined, pillClassName: string) => {
    if (value === undefined || value === null) return null;
    const pct = normalizeScore(value);
    return (
      <span className={`search-result__pill ${pillClassName}`}>
        {label} {pct}%
      </span>
    );
  };

  // Tag input handlers
  const handleAddInclude = useCallback((value: string) => {
    const terms = value.split(',').map((t) => t.trim()).filter(Boolean);
    if (terms.length > 0) {
      setIncludeTags((prev) => [...new Set([...prev, ...terms])]);
      setIncludeInput('');
    }
  }, []);

  const handleAddExclude = useCallback((value: string) => {
    const terms = value.split(',').map((t) => t.trim()).filter(Boolean);
    if (terms.length > 0) {
      setExcludeTags((prev) => [...new Set([...prev, ...terms])]);
      setExcludeInput('');
    }
  }, []);

  const hasActiveQuery = query.trim().length > 0 || rawResults.length > 0;

  // Search now works locally via PostgreSQL full-text search
  return (
    <div className={`archive-search ${className}`}>
      {/* Search Input */}
      <div className="archive-search-input">
        <div className="archive-search-input__field">
          <span className="archive-search-input__icon">&#x1F50D;</span>
          <input
            ref={inputRef}
            type="text"
            className="archive-search-input__input"
            placeholder="Search transcripts, conversations, media..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search archive"
          />
          {query && (
            <button
              className="archive-search-input__clear"
              onClick={() => { setQuery(''); setRawResults([]); setIncludeTags([]); setExcludeTags([]); }}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Keyword Filters — shown when query is active */}
      {hasActiveQuery && (
        <div className="search-filters">
          <div className="search-filters__row">
            <span className="search-filters__label search-filters__label--include">+Must have:</span>
            <div className="search-filters__tags">
              {includeTags.map((tag) => (
                <span key={tag} className="search-filters__tag search-filters__tag--include">
                  {tag}
                  <button
                    className="search-filters__tag-remove"
                    onClick={() => setIncludeTags((prev) => prev.filter((t) => t !== tag))}
                    aria-label={`Remove include filter: ${tag}`}
                  >✕</button>
                </span>
              ))}
              <input
                type="text"
                className="search-filters__input"
                placeholder="Add keyword..."
                value={includeInput}
                onChange={(e) => setIncludeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddInclude(includeInput); }}
              />
            </div>
          </div>
          <div className="search-filters__row">
            <span className="search-filters__label search-filters__label--exclude">-Exclude:</span>
            <div className="search-filters__tags">
              {excludeTags.map((tag) => (
                <span key={tag} className="search-filters__tag search-filters__tag--exclude">
                  {tag}
                  <button
                    className="search-filters__tag-remove"
                    onClick={() => setExcludeTags((prev) => prev.filter((t) => t !== tag))}
                    aria-label={`Remove exclude filter: ${tag}`}
                  >✕</button>
                </span>
              ))}
              <input
                type="text"
                className="search-filters__input"
                placeholder="Add keyword..."
                value={excludeInput}
                onChange={(e) => setExcludeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddExclude(excludeInput); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Filter Toggle */}
      <div className="search-results__header">
        <span className="search-results__count">
          {isLoading
            ? 'Searching...'
            : filteredCount > 0
              ? `${filteredResults.length} results (${filteredCount} filtered)`
              : `${filteredResults.length} results`
          }
        </span>
        <div className="search-mode-toggle">
          {(['all', 'transcripts', 'conversations', 'media'] as SearchFilter[]).map((f) => (
            <button
              key={f}
              className={`search-mode-toggle__option ${filter === f ? 'search-mode-toggle__option--active' : ''}`}
              onClick={() => handleFilterChange(f)}
            >
              {f === 'all' && 'All'}
              {f === 'transcripts' && 'Transcripts'}
              {f === 'conversations' && 'Conversations'}
              {f === 'media' && 'Media'}
            </button>
          ))}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="panel__empty">
          <div className="panel__empty-icon">&#x26A0;&#xFE0F;</div>
          <div className="panel__empty-title">Search Error</div>
          <div className="panel__empty-description">{error}</div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="panel__loading">
          <div className="panel__loading-spinner" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && query && filteredResults.length === 0 && (
        <div className="panel__empty">
          <div className="panel__empty-icon">&#x1F50D;</div>
          <div className="panel__empty-title">No Results</div>
          <div className="panel__empty-description">
            Try different keywords or adjust filters
          </div>
        </div>
      )}

      {/* Initial State */}
      {!query && !isLoading && rawResults.length === 0 && (
        <div className="panel__empty">
          <div className="panel__empty-icon">&#x1F50D;</div>
          <div className="panel__empty-title">Search &amp; Harvest</div>
          <div className="panel__empty-description">
            Search your archive. Click a result to load the full document.
          </div>
        </div>
      )}

      {/* Ranked Results */}
      {!isLoading && filteredResults.length > 0 && (
        <div className="search-results__list" role="listbox" aria-label="Search results">
          {filteredResults.map((result, idx) => {
            const rank = idx + 1;
            const score = reviewedScore(result);
            const sb = result.scoreBreakdown;
            const title = resultTitle(result);
            const preview = result.text.slice(0, 200) + (result.text.length > 200 ? '...' : '');
            const isCardLoading = loadingId === result.id;

            return (
              <div
                key={result.id}
                className={`search-result search-result--harvest ${isCardLoading ? 'search-result--loading' : ''}`}
                role="option"
                tabIndex={0}
                onClick={() => handleCardClick(result)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCardClick(result); }}
              >
                {/* Rank badge + content */}
                <div className="search-result__harvest-layout">
                  <div className="search-result__rank-badge">
                    #{rank}
                  </div>
                  <div className="search-result__harvest-body">
                    <div className="search-result__harvest-title">{title}</div>

                    {/* Score pills — clamped to 0-100% */}
                    <div className="search-result__pills">
                      {scorePill('KW', sb?.sparseScore, 'search-result__pill--kw')}
                      {scorePill('SEM', sb?.denseScore, 'search-result__pill--sem')}
                      <span className="search-result__pill search-result__pill--score">
                        Score {normalizeScore(score)}%
                      </span>
                    </div>

                    <div className="search-result__content">{preview}</div>

                    {/* Source breadcrumb */}
                    <div className="search-result__meta">
                      {result.provenance?.threadTitle && (
                        <span>{result.provenance.threadTitle}</span>
                      )}
                      {result.provenance?.authorRole && (
                        <>
                          {result.provenance?.threadTitle && (
                            <span className="search-result__meta-sep">&rsaquo;</span>
                          )}
                          <span>{result.provenance.authorRole}</span>
                        </>
                      )}
                      {isCardLoading && <span className="search-result__loading-text">Loading...</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ArchiveSearch;
